import React, { useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * BackgroundQuestionsHelper - Text entry fields for character background questions
 *
 * Displays class-specific background prompts as text input fields
 * to help players flesh out their character's backstory.
 *
 * @param {Array} questions - Array of question strings
 * @param {Object} currentAnswers - Current answers { question1, question2, ... }
 * @param {Function} onAnswerChange - Callback(questionIndex, value) when user types
 */
function BackgroundQuestionsHelper({ questions, currentAnswers = {}, onAnswerChange }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!questions || questions.length === 0) return null;

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors text-sm font-medium"
      >
        <Sparkles className="w-4 h-4" />
        Need inspiration?
        <ChevronDown className={cn(
          "w-4 h-4 transition-transform",
          isExpanded && "rotate-180"
        )} />
      </button>

      {isExpanded && (
        <div className="mt-3 p-4 bg-gradient-to-br from-gray-900 to-black border border-purple-500/50 rounded-lg space-y-4">
          <p className="text-xs text-gray-400">
            Answer these questions to develop your character's backstory. Your answers will help shape their narrative.
          </p>

          {questions.map((question, index) => (
            <div key={index}>
              <label className="block text-xs font-medium text-purple-300 mb-2">
                {index + 1}. {question}
              </label>
              <textarea
                value={currentAnswers[`question${index}`] || ''}
                onChange={(e) => onAnswerChange(index, e.target.value)}
                placeholder="Your answer..."
                rows={2}
                className="w-full px-3 py-2 text-xs bg-gray-800 border border-gray-600 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>
          ))}

          <div className="pt-2 border-t border-gray-700">
            <p className="text-xs text-gray-400 italic">
              Tip: These answers help you develop a richer backstory and can inspire roleplay moments!
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default BackgroundQuestionsHelper;
